// components/Header.tsx

import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Badge,
  Tooltip,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Engineering,
  WorkspacePremium,
  Settings,
  CardMembership,
  Logout,
  Login as LoginIcon,
  Notifications,
  Chat,
  ManageAccounts,
  Info,
  OpenInNew,
  PersonSearch,
  Person,
} from '@mui/icons-material';
import { createCustomerPortalSession } from '../api/subscription';
import { useAuth } from '../contexts/AuthContext';
import { useNotice } from '../contexts/NoticeContext';
import { fetchWithSessionHandling } from '../utils/authFetch';
import { getApiDateTimeTimestamp, parseApiDateTime } from '../utils/apiDateTime';

const parseEnvBoolean = (value?: string) => {
  if (value == null) {
    return true;
  }
  const normalized = value.trim().toLowerCase();
  return !['false', '0', 'no', 'off'].includes(normalized);
};

const TRIAL_TIME_UPDATE_INTERVAL_MS = 60 * 1000;
const JST_TIME_ZONE = 'Asia/Tokyo';
const createNoticeManagementUrl = () => `/notice_management/?v=${Date.now()}`;

const formatTrialTimeRemaining = (trialExpiresAt?: string | null, now = Date.now()) => {
  if (!trialExpiresAt) {
    return null;
  }

  const expiresAt = getApiDateTimeTimestamp(trialExpiresAt);
  if (expiresAt == null) {
    return null;
  }

  const diffMs = expiresAt - now;
  if (diffMs < 60 * 1000) {
    return null;
  }

  const totalMinutes = Math.floor(diffMs / (60 * 1000));
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}日`);
  }
  if (hours > 0) {
    parts.push(`${hours}時間`);
  }
  if (minutes > 0 || parts.length === 0) {
    parts.push(`${minutes}分`);
  }

  return `試用期間終了まで 残り ${parts.join(' ')}`;
};

const formatPreviousLoginAt = (previousLoginAt?: string | null) => {
  const date = parseApiDateTime(previousLoginAt);
  if (!date) {
    return null;
  }

  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .formatToParts(date)
    .reduce<Record<string, string>>((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

  if (!parts.year || !parts.month || !parts.day || !parts.hour || !parts.minute || !parts.second) {
    return null;
  }

  return `前回ログイン：${parts.year}/${parts.month}/${parts.day}/ ${parts.hour}:${parts.minute}:${parts.second}`;
};

const Header: React.FC = () => {
  const { user, logout, isAuthenticated, token } = useAuth();
  const { unreadCount, newCount } = useNotice();
  const navigate = useNavigate();
  const location = useLocation();
  const environment = (process.env.REACT_APP_ENVIRONMENT || 'server').trim().toLowerCase();
  const isLocalEnvironment = environment === 'local';
  const isNoticeManagementEnabledByConfig = parseEnvBoolean(
    process.env.REACT_APP_ENABLE_NOTICE_MANAGEMENT
  );
  const isNoticeManagementEnabled = !isLocalEnvironment && isNoticeManagementEnabledByConfig;
  const noticeManagementDisabledReason = (() => {
    if (isLocalEnvironment) {
      return 'ローカル環境ではお知らせ管理を利用できません。';
    }
    if (!isNoticeManagementEnabledByConfig) {
      return '環境設定によりお知らせ管理が無効化されています。';
    }
    return '';
  })();
  const [isOpeningPortal, setIsOpeningPortal] = React.useState(false);

  const [adminMenuAnchorEl, setAdminMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [appInfoMenuAnchorEl, setAppInfoMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [trialTimeRemaining, setTrialTimeRemaining] = React.useState<string | null>(() =>
    formatTrialTimeRemaining(user?.trialExpiresAt)
  );
  const previousLoginText = React.useMemo(
    () => formatPreviousLoginAt(user?.previousLoginAt),
    [user?.previousLoginAt]
  );
  const isDefaultAdminUser = Boolean(user?.isDefaultAdmin);
  const isViewer = Boolean(user?.isViewer);
  const isExpired = Boolean(user?.isExpired);
  const hasPriorSubscriptionContract = Boolean(user?.hasPriorSubscriptionContract);
  const shouldOfferSubscriptionPurchase = Boolean(
    user &&
      !isViewer &&
      !user.hasActiveSubscription &&
      !hasPriorSubscriptionContract
  );
  const subscriptionMenuLabel = shouldOfferSubscriptionPurchase
    ? 'サブスクリプション購入はこちらから'
    : 'サブスクリプション管理';
  const shouldShowTrialTimeRemaining = Boolean(
    user?.isTrial && !user?.hasActiveSubscription && user?.trialExpiresAt
  );

  React.useEffect(() => {
    if (!shouldShowTrialTimeRemaining) {
      setTrialTimeRemaining(null);
      return;
    }

    const updateTrialTimeRemaining = () => {
      setTrialTimeRemaining(formatTrialTimeRemaining(user?.trialExpiresAt));
    };

    updateTrialTimeRemaining();
    const timer = window.setInterval(updateTrialTimeRemaining, TRIAL_TIME_UPDATE_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [shouldShowTrialTimeRemaining, user?.trialExpiresAt]);

  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAdminMenuAnchorEl(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchorEl(null);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };
  const handleOpenSubscriptionPortal = async () => {
    handleUserMenuClose();
    if (isOpeningPortal) {
      return;
    }

    setIsOpeningPortal(true);
    try {
      const portalUrl = await createCustomerPortalSession();
      window.location.assign(portalUrl);
    } catch {
      window.alert('契約管理画面を開けませんでした。時間をおいて再度お試しください。');
    } finally {
      setIsOpeningPortal(false);
    }
  };



  const handleAppInfoMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAppInfoMenuAnchorEl(event.currentTarget);
  };

  const handleAppInfoMenuClose = () => {
    setAppInfoMenuAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNoticeClick = () => {
    navigate('/notice');
  };

  const buildApiPath = (path: string) => {
    const base = process.env.REACT_APP_API_URL || '';
    if (!base) return path;
    return `${base.replace(/\/$/, '')}${path}`;
  };

  const handleOpenNoticeManagement = async () => {
    handleAdminMenuClose();
    if (!isNoticeManagementEnabled) {
      return;
    }

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const { response, sessionExpired } = await fetchWithSessionHandling(
        buildApiPath('/notice_server/sessions'),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
        { logout },
      );

      if (sessionExpired) {
        return;
      }

      if (!response.ok) {
        throw new Error('failed to create notice session');
      }

      const payload = await response.json();
      sessionStorage.setItem('notice_session', JSON.stringify(payload));
      window.location.assign(createNoticeManagementUrl());
    } catch (error) {
      console.error('お知らせ管理セッション生成に失敗しました', error);
      alert('お知らせ管理画面を開けませんでした。再度お試しください。');
    }
  };

  // 未読件数は NoticeContext で管理

  return (
    <AppBar position="static">
      <Toolbar>
        <Typography
          variant="h6"
          component="a"
          href="https://ict.nslab.co.jp"
          sx={{ flexGrow: 1, color: 'inherit', textDecoration: 'none' }}
        >
          手指衛生データ分析アプリ
        </Typography>

        {isAuthenticated ? (
          <>
            <Button
              color="inherit"
              component={RouterLink}
              to="/"
              startIcon={<Home />}
              sx={{ mr: 1 }}
            >
              ホーム
            </Button>

            <Badge
              color="error"
              badgeContent={unreadCount}
              invisible={unreadCount === 0}
              sx={{ mr: 1 }}
            >
              <Button
                color="inherit"
                onClick={handleNoticeClick}
                startIcon={<Notifications />}
              >
                お知らせ
              </Button>
            </Badge>

            {user?.isAdmin && (
              <>
                <Button
                  color="inherit"
                  onClick={handleAdminMenuOpen}
                  startIcon={<Settings />}
                  sx={{ mr: 1 }}
                >
                  管理者メニュー
                </Button>
                <Menu
                  anchorEl={adminMenuAnchorEl}
                  open={Boolean(adminMenuAnchorEl)}
                  onClose={handleAdminMenuClose}
                >
                  {isNoticeManagementEnabled ? (
                    <MenuItem onClick={handleOpenNoticeManagement}>
                      お知らせ管理へ
                    </MenuItem>
                  ) : (
                    <Tooltip
                      title={noticeManagementDisabledReason}
                      placement="right"
                      disableHoverListener={!noticeManagementDisabledReason}
                    >
                      <span>
                        <MenuItem onClick={handleOpenNoticeManagement} disabled>
                          お知らせ管理へ
                        </MenuItem>
                      </span>
                    </Tooltip>
                  )}
                  <MenuItem
                    component={RouterLink}
                    to="/db-admin"
                    onClick={handleAdminMenuClose}
                  >
                    DB操作画面へ
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/db-view"
                    onClick={handleAdminMenuClose}
                  >
                    DB閲覧画面へ
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/login-history"
                    onClick={handleAdminMenuClose}
                  >
                    ログイン履歴
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/analytics-log"
                    onClick={handleAdminMenuClose}
                  >
                    導線ログ
                  </MenuItem>
                </Menu>
              </>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                color="inherit"
                href="https://nslab.co.jp/inquiry/"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<Chat />}
                sx={{ mr: 1 }}
              >
                お問い合わせ
              </Button>
              <Button
                color="inherit"
                onClick={handleAppInfoMenuOpen}
                startIcon={<Info />}
                sx={{ mr: 1 }}
              >
                アプリ情報
              </Button>
              <Menu
                anchorEl={appInfoMenuAnchorEl}
                open={Boolean(appInfoMenuAnchorEl)}
                onClose={handleAppInfoMenuClose}
              >
                <MenuItem
                  component="a"
                  href="https://nslab.co.jp/services/ict-data-analysis/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleAppInfoMenuClose}
                >
                  <ListItemIcon>
                    <OpenInNew fontSize="small" />
                  </ListItemIcon>
                  このアプリについて
                </MenuItem>
                <MenuItem
                  component="a"
                  href="https://nslab.co.jp/services/ict-data-analysis/terms-of-use/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleAppInfoMenuClose}
                >
                  <ListItemIcon>
                    <OpenInNew fontSize="small" />
                  </ListItemIcon>
                  利用規約
                </MenuItem>
                <MenuItem
                  component="a"
                  href="https://nslab.co.jp/services/ict-data-analysis/privacy_policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleAppInfoMenuClose}
                >
                  <ListItemIcon>
                    <OpenInNew fontSize="small" />
                  </ListItemIcon>
                  プライバシーポリシー
                </MenuItem>
                <MenuItem
                  component="a"
                  href="https://nslab.co.jp/services/ict-data-analysis/legal_disclosure/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleAppInfoMenuClose}
                >
                  <ListItemIcon>
                    <OpenInNew fontSize="small" />
                  </ListItemIcon>
                  特定商取引法に基づく表記
                </MenuItem>
              </Menu>
              <Button
                color="inherit"
                startIcon={
                  user?.isAdmin ? (
                    <Engineering />
                  ) : user?.isVip ? (
                    <WorkspacePremium />
                  ) : isViewer ? (
                    <PersonSearch />
                  ) : (
                    <Person />
                  )
                }
                onClick={handleUserMenuOpen}
              >
                ユーザーメニュー
              </Button>
              <Menu
                anchorEl={userMenuAnchorEl}
                open={Boolean(userMenuAnchorEl)}
                onClose={handleUserMenuClose}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    minWidth: 200,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {user?.username}
                  </Typography>
                  {previousLoginText && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.25 }}
                    >
                      {previousLoginText}
                    </Typography>
                  )}
                  {isExpired && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 0.25, color: 'error.main' }}
                    >
                      サブスクリプションの有効期限が切れています
                    </Typography>
                  )}
                  {trialTimeRemaining && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.25 }}
                    >
                      {trialTimeRemaining}
                    </Typography>
                  )}
                </Box>
                {isDefaultAdminUser ? (
                  <MenuItem disabled>
                    <ListItemIcon>
                      <ManageAccounts fontSize="small" />
                    </ListItemIcon>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <span>アカウント設定</span>
                      <Typography variant="caption" color="text.secondary">
                        初期管理者は変更できません
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : isViewer ? (
                  <MenuItem disabled>
                    <ListItemIcon>
                      <ManageAccounts fontSize="small" />
                    </ListItemIcon>
                    アカウント設定
                  </MenuItem>
                ) : (
                  <MenuItem
                    component={RouterLink}
                    to="/user-settings"
                    onClick={handleUserMenuClose}
                  >
                    <ListItemIcon>
                      <ManageAccounts fontSize="small" />
                    </ListItemIcon>
                    アカウント設定
                  </MenuItem>
                )}
                {user && !user.isVip
                  ? isViewer ? (
                      <MenuItem disabled>
                        <ListItemIcon>
                          <CardMembership fontSize="small" />
                        </ListItemIcon>
                        {subscriptionMenuLabel}
                      </MenuItem>
                    ) : shouldOfferSubscriptionPurchase ? (
                      <MenuItem
                        component={RouterLink}
                        to="/subscription"
                        onClick={handleUserMenuClose}
                      >
                        <ListItemIcon>
                          <CardMembership fontSize="small" />
                        </ListItemIcon>
                        {subscriptionMenuLabel}
                      </MenuItem>
                    ) : !user.hasActiveSubscription || isExpired ? (
                      <MenuItem
                        component={RouterLink}
                        to="/subscription"
                        onClick={handleUserMenuClose}
                      >
                        <ListItemIcon>
                          <CardMembership fontSize="small" />
                        </ListItemIcon>
                        {subscriptionMenuLabel}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        onClick={() => { void handleOpenSubscriptionPortal(); }}
                        disabled={isOpeningPortal}
                      >
                        <ListItemIcon>
                          <CardMembership fontSize="small" />
                        </ListItemIcon>
                        {isOpeningPortal ? '契約管理画面を準備中…' : subscriptionMenuLabel}
                      </MenuItem>
                    )
                  : null}
                <MenuItem
                  onClick={() => {
                    handleUserMenuClose();
                    handleLogout();
                  }}
                >
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  ログアウト
                </MenuItem>
              </Menu>
            </Box>
          </>
        ) : (
          <>
            <Badge
              color="error"
              badgeContent={newCount}
              invisible={newCount === 0}
              sx={{ mr: 1 }}
            >
              <Button
                color="inherit"
                onClick={handleNoticeClick}
                startIcon={<Notifications />}
              >
                お知らせ
              </Button>
            </Badge>
            <Button
              color="inherit"
              href="https://nslab.co.jp/inquiry/"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Chat />}
              sx={{ mr: 1 }}
            >
              お問い合わせ
            </Button>
            <Button
              color="inherit"
              onClick={handleAppInfoMenuOpen}
              startIcon={<Info />}
              sx={{ mr: 1 }}
            >
              アプリ情報
            </Button>
            <Menu
              anchorEl={appInfoMenuAnchorEl}
              open={Boolean(appInfoMenuAnchorEl)}
              onClose={handleAppInfoMenuClose}
            >
              <MenuItem
                component="a"
                href="https://nslab.co.jp/services/ict-data-analysis/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleAppInfoMenuClose}
              >
                <ListItemIcon>
                  <OpenInNew fontSize="small" />
                </ListItemIcon>
                このアプリについて
              </MenuItem>
              <MenuItem
                component="a"
                href="https://nslab.co.jp/services/ict-data-analysis/terms-of-use/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleAppInfoMenuClose}
              >
                <ListItemIcon>
                  <OpenInNew fontSize="small" />
                </ListItemIcon>
                利用規約
              </MenuItem>
              <MenuItem
                component="a"
                href="https://nslab.co.jp/services/ict-data-analysis/privacy_policy/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleAppInfoMenuClose}
              >
                <ListItemIcon>
                  <OpenInNew fontSize="small" />
                </ListItemIcon>
                プライバシーポリシー
              </MenuItem>
              <MenuItem
                component="a"
                href="https://nslab.co.jp/services/ict-data-analysis/legal_disclosure/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleAppInfoMenuClose}
              >
                <ListItemIcon>
                  <OpenInNew fontSize="small" />
                </ListItemIcon>
                特定商取引法に基づく表記
              </MenuItem>
            </Menu>
            {location.pathname !== '/login' && (
              <Button
                color="inherit"
                component={RouterLink}
                to="/login"
                startIcon={<LoginIcon />}
              >
                ログイン
              </Button>
            )}
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
